import { track } from '@/lib/telemetry/track';
import type { SearchResult } from '@/lib/search/types';

/**
 * What people search for, and whether we had an answer.
 *
 * The keyword map can never be exhaustive, so the useful question is not "did
 * we think of every synonym" but "which ones are we missing" — and that is a
 * measurement, not a guess. Empty searches are the whole signal: each one is a
 * director asking for something in words the index does not know.
 *
 * Privacy: the raw query text is recorded only when nothing matched. A query
 * that found a page tells us nothing we need, and once a record layer lands,
 * successful queries will routinely contain children's and staff names.
 */
const MAX_LOGGED_QUERY = 80;

export function logSearch(query: string, results: SearchResult[]): void {
  const trimmed = query.trim();
  if (trimmed.length < 2) return;

  const empty = results.length === 0;
  track('search', {
    name: empty ? 'search_empty' : 'search',
    metadata: {
      length: trimmed.length,
      results: results.length,
      locked: results.filter((r) => r.locked).length,
      top: results[0]?.href ?? null,
      ...(empty ? { query: trimmed.slice(0, MAX_LOGGED_QUERY) } : {}),
    },
  });
}

/** A selection is the ground truth for whether the ranking was right. */
export function logSearchSelect(query: string, result: SearchResult, rank: number): void {
  track('search', {
    name: 'search_select',
    route: result.href,
    metadata: {
      length: query.trim().length,
      rank,
      locked: result.locked ?? false,
      hub: result.hub ?? null,
    },
  });
}
